import { z } from 'zod';
import type {
	AuthorizerConfig,
	AuthorizerHooks,
	NavigationPort,
	RedirectLoopContext,
} from './ports.js';
import type { AuthorizerProps, PolicyObject } from './types.js';
import { clearVisitedPaths, getVisitedPaths, markPathVisited } from './visited-paths.js';

const SchemaPolicyObject = z.object({
	policy: z.function().returns(z.promise(z.boolean())),
	policyExceptionHandler: z.function().returns(z.promise(z.string())).optional(),
});

const SchemaAuthorizerProps = z.object({
	policies: SchemaPolicyObject,
	routePath: z.string(),
});

/**
 * Framework-agnostic route authorizer: validates policy inputs, evaluates
 * per-route policies, detects redirect cycles via sessionStorage, and delegates
 * navigation and telemetry to injected ports.
 */
export class Authorizer {
	readonly #navigation: NavigationPort;
	readonly #hooks: AuthorizerHooks;
	readonly #basePath: string;
	readonly #shouldReplaceStateOnRedirect: (url: string) => boolean;
	readonly #propsSchema = SchemaAuthorizerProps;

	policies: PolicyObject;
	routePath: string;

	constructor(props: AuthorizerProps, config: AuthorizerConfig) {
		this.#navigation = config.navigation;
		this.#hooks = config.hooks;
		this.#basePath = config.basePath ?? '';
		this.#shouldReplaceStateOnRedirect = config.shouldReplaceStateOnRedirect ?? (() => false);
		this.validate(props);
		this.policies = props.policies;
		this.routePath = props.routePath;
	}

	/** Validates constructor props against the Zod schema. */
	validate(props: AuthorizerProps): boolean {
		const validation = this.#propsSchema.safeParse(props);
		if (!validation.success) {
			this.#hooks.onInvalidInput?.(`Invalid input: ${validation.error.message}`);
			return false;
		}
		return true;
	}

	/**
	 * Authorizes the current route in the browser.
	 * @returns true when access is allowed, false when a redirect was triggered.
	 */
	async browserAuthorize(): Promise<boolean> {
		const isActionAllow = await this.policies.policy();

		if (isActionAllow) {
			clearVisitedPaths();
			return true;
		}

		const currentPath = this.#basePath ? this.#basePath + this.routePath : this.routePath;

		let pathToRedirect = this.policies.policyExceptionHandler
			? await this.policies.policyExceptionHandler()
			: await this.#hooks.onAccessDenied();

		const hasCycle = markPathVisited(currentPath);
		const loopContext: RedirectLoopContext = {
			currentPath,
			hasCycle,
			pathToRedirect,
			visitedPaths: [...getVisitedPaths()],
		};

		if (hasCycle) {
			this.#hooks.onRedirectLoop(loopContext);
			clearVisitedPaths();
			pathToRedirect = await this.#hooks.onCycleDetected(loopContext);
		} else if (!pathToRedirect || currentPath === pathToRedirect) {
			this.#hooks.onRedirectLoop(loopContext);
			pathToRedirect = await this.#hooks.onAccessDenied();
		}

		this.#hooks.onBeforeRedirect?.(pathToRedirect);
		const replaceState = this.#shouldReplaceStateOnRedirect(pathToRedirect);
		await this.#navigation.navigate(pathToRedirect, { replaceState });

		return false;
	}
}
